import React, { useEffect, useRef, useState } from 'react'
import * as api from 'api'
import s from './styles.module.scss'

function formatMemberLabel(user) {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return name || user.userRefId || user.email || 'BCN Member'
}

function formatMemberMeta(user) {
  const bits = []
  if (user.userRefId) bits.push(`BCN ${user.userRefId}`)
  if (user.phoneNo) bits.push(`+91-${user.phoneNo}`)
  if (user.email) bits.push(user.email)
  return bits.join(' · ')
}

export default function MemberSearchField({ value, onChange, label = 'Link to Member (optional)' }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState(null)
  const [showEmpty, setShowEmpty] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (!value) {
      setSelected(null)
      return
    }
    if (selected?._id === value) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await api.admin.user.fetchSingle({ id: value })
        if (!cancelled && res?.code === 200 && res.payload) {
          setSelected(res.payload)
        }
      } catch {
        if (!cancelled) setSelected(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [value, selected?._id])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      setShowEmpty(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await api.admin.user.fetchAll({
          searchQuery: trimmed,
          limit: 8,
          pageNo: 1,
        })
        if (res?.code === 200) {
          const users = res.payload?.Users || []
          setResults(users)
          setShowEmpty(users.length === 0)
        } else {
          setResults([])
          setShowEmpty(true)
        }
      } catch {
        setResults([])
        setShowEmpty(true)
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const handleSelect = user => {
    setSelected(user)
    setQuery('')
    setResults([])
    setShowEmpty(false)
    onChange(user._id)
  }

  const handleClear = () => {
    setSelected(null)
    setQuery('')
    setResults([])
    setShowEmpty(false)
    onChange('')
  }

  return (
    <div className={s.wrap}>
      <label className={s.label}>{label}</label>

      {selected ? (
        <div className={s.selected}>
          <div>
            <strong>{formatMemberLabel(selected)}</strong>
            <span>{formatMemberMeta(selected)}</span>
          </div>
          <button type="button" className={s.clearBtn} onClick={handleClear}>
            Remove
          </button>
        </div>
      ) : (
        <>
          <div className={s.searchRow}>
            <span className="material-icons-outlined">person_search</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, phone, email, or BCN ID"
            />
          </div>
          <p className={s.hint}>
            {searching ? 'Searching members…' : 'Type at least 2 characters to find a member'}
          </p>
          {results.length > 0 && (
            <div className={s.results}>
              {results.map(user => (
                <button
                  type="button"
                  key={user._id}
                  className={s.resultBtn}
                  onClick={() => handleSelect(user)}
                >
                  <strong>{formatMemberLabel(user)}</strong>
                  <span>{formatMemberMeta(user)}</span>
                </button>
              ))}
            </div>
          )}
          {showEmpty && !searching && <div className={s.empty}>No members found. Try another name or phone number.</div>}
        </>
      )}
    </div>
  )
}
